import { createContext, useContext, useMemo, useState } from 'react';
import type { ThrowableId, VoiceTauntId } from '@big2/shared';
import { useGameState } from '../../net/GameProvider.js';
import { useI18n } from '../../i18n.js';
import { TableInteractionOverlay } from './TableInteractionOverlay.js';
import { TableInteractionPicker, type TargetPlayerInfo } from './TableInteractionPicker.js';

interface TableInteractionsContextValue {
  openPicker: (target?: TargetPlayerInfo) => void;
  closePicker: () => void;
  isVip: boolean;
  freeThrowsRemaining: number;
}

const TableInteractionsContext = createContext<TableInteractionsContextValue | null>(null);

export function useTableInteractions() {
  const ctx = useContext(TableInteractionsContext);
  if (!ctx) {
    throw new Error('useTableInteractions must be used within TableInteractionsProvider');
  }
  return ctx;
}

interface TableInteractionsProviderProps {
  children: React.ReactNode;
  onOpenProfile?: () => void;
  containerRef?: React.RefObject<HTMLElement | null>;
}

export function TableInteractionsProvider({
  children,
  onOpenProfile,
  containerRef,
}: TableInteractionsProviderProps) {
  const { state, actions } = useGameState();
  const { lang } = useI18n();
  const isMn = lang === 'mn';
  const { game, profile, tableThrows, voiceTaunts } = state;

  const isVip = profile?.tier === 'paid';
  const [selectedTarget, setSelectedTarget] = useState<TargetPlayerInfo | null>(null);

  // All opponents in current game
  const opponents: TargetPlayerInfo[] = useMemo(() => {
    if (!game) return [];
    return game.players
      .filter((p) => p.playerId !== game.myPlayerId)
      .map((p) => ({
        playerId: p.playerId,
        name: p.name,
        seat: p.seat,
        tier: p.tier,
      }));
  }, [game]);

  const freeThrowsRemaining = 0;

  const openPicker = (target?: TargetPlayerInfo) => {
    if (target) {
      setSelectedTarget(target);
    } else if (opponents.length > 0) {
      // Default to active player if opponent, otherwise first opponent
      const activeOpponent = opponents.find((o) => o.playerId === game?.activePlayerId);
      setSelectedTarget(activeOpponent ?? opponents[0]);
    }
  };

  const closePicker = () => setSelectedTarget(null);

  const handleThrow = (throwableId: ThrowableId) => {
    if (!selectedTarget || !isVip) return;
    actions.sendTableThrow(selectedTarget.seat, throwableId);
  };

  const handleTaunt = (tauntId: VoiceTauntId) => {
    if (!selectedTarget || !isVip) return;
    actions.sendVoiceTaunt(tauntId, selectedTarget.seat);
  };

  const contextValue = useMemo(
    () => ({
      openPicker,
      closePicker,
      isVip,
      freeThrowsRemaining,
    }),
    [isVip, freeThrowsRemaining, opponents, game?.activePlayerId],
  );

  return (
    <TableInteractionsContext.Provider value={contextValue}>
      {children}

      {/* Floating Animations Overlay */}
      <TableInteractionOverlay
        tableThrows={tableThrows}
        voiceTaunts={voiceTaunts}
        game={game}
        isMn={isMn}
        containerRef={containerRef}
      />

      {/* Popover / Modal Picker */}
      {selectedTarget && (
        <TableInteractionPicker
          target={selectedTarget}
          isVip={isVip}
          freeThrowsRemaining={freeThrowsRemaining}
          onClose={closePicker}
          onThrow={handleThrow}
          onTaunt={handleTaunt}
          onOpenVipModal={() => {
            closePicker();
            onOpenProfile?.();
          }}
          isMn={isMn}
        />
      )}
    </TableInteractionsContext.Provider>
  );
}

/** Action bar button to trigger table throw & voice taunt */
export function TableInteractionBarButton({
  isMn = true,
}: {
  isMn?: boolean;
}) {
  const { openPicker, freeThrowsRemaining, isVip } = useTableInteractions();
  const label = isMn ? 'Ширээний шидэлт, тавлалт' : 'Throw & Voice Taunt';

  return (
    <button
      type="button"
      className="table-throw-action-bar-btn"
      onClick={() => openPicker()}
      title={`${label} (${isVip ? '👑 VIP' : `${freeThrowsRemaining}/1`})`}
      aria-label={label}
    >
      <span>🎯</span>
    </button>
  );
}
